import { memo, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';
import { AddChapter } from 'components/courses/common';
import { useEditorDispatch } from 'components/editor';
import { EditorSearch } from 'components/editor/search';
import { EditorChaptersChapter, EditorChaptersOrder } from '.';

import type { GetCourseTranslationProgressRes } from 'shared-types/responses';
import type { Editor } from 'types';

interface Props {
  activeLanguageVersion: Editor['editorActiveLanguageVersion'];
  chapters: Editor['editorChapters'];
  currentChapter: Editor['editorCurrentChapter'];
  currentHeadings: { id: number; level: 'H2' | 'H3'; text: string }[];
  previewType: Editor['editorPreviewType'];
  previewVersion: Editor['editorPreviewVersion'];
  translationProgressData: GetCourseTranslationProgressRes;
}

const EditorChapters = ({
  activeLanguageVersion,
  chapters,
  currentChapter,
  currentHeadings,
  previewType,
  previewVersion,
  translationProgressData
}: Props) => {
  const editorDispatch = useEditorDispatch();

  const { messages, theme } = useReduxState();

  const [isChaptersExpanded, setIsChaptersExpanded] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const handleToggleChaptersKeyDown = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    // check if enter or spacebar
    if (ev.keyCode === 13 || ev.keyCode === 32) {
      ev.preventDefault();
      toggleChapters();
    }
  };

  const handleSearch = (state: boolean) => setIsSearching(state);

  const handleEditorView = () => {
    editorDispatch({
      type: 'updateContext',
      update: { editorActiveView: 'course-version-history' }
    });
  };

  const toggleChapters = () => setIsChaptersExpanded(!isChaptersExpanded);

  return (
    <div className="chapters">
      {previewType === 'course' ? null : (
        <EditorSearch
          disabled={previewType === 'chapter'}
          onSearch={handleSearch}
        />
      )}

      {!isSearching ? (
        <>
          <div
            aria-expanded={isChaptersExpanded}
            className="header header-toggle focus-box-shadow"
            onClick={toggleChapters}
            onKeyDown={handleToggleChaptersKeyDown}
            role="button"
            tabIndex={0}
          >
            <div className="header-title">{messages.commonChapters}</div>
            <div className="header-icon">
              <Icon
                fixedWidth={true}
                icon={isChaptersExpanded ? 'chevron-up' : 'chevron-down'}
              />
            </div>
          </div>

          <div
            className={`active-chapter ${!isChaptersExpanded ? 'visible' : ''}`}
          >
            <EditorChaptersChapter
              // Remount when the publish date changes, so the
              // published status is read against a fresh time
              key={`${currentChapter.id}-${
                'publishDate' in currentChapter
                  ? currentChapter.publishDate
                  : ''
              }`}
              activeLanguageVersion={activeLanguageVersion}
              chapter={currentChapter}
              currentHeadings={currentHeadings}
              isActive={true}
              languageVersionTranslationProgress={
                activeLanguageVersion
                  ? translationProgressData?.progressByLanguage[
                      activeLanguageVersion
                    ]?.progressByChapterId?.[currentChapter.id]
                  : undefined
              }
              previewType={previewType}
              previewVersion={previewVersion}
            />
          </div>

          <div
            className={`chapters-list ${isChaptersExpanded ? 'expanded' : ''} ${
              previewType === 'course' || Boolean(activeLanguageVersion)
                ? 'without-actions'
                : ''
            }`}
          >
            <div className="chapters-list-content">
              {chapters.map((chapter) => (
                <EditorChaptersChapter
                  activeLanguageVersion={activeLanguageVersion}
                  chapter={chapter}
                  currentHeadings={currentHeadings}
                  isActive={chapter.id === currentChapter.id}
                  key={`${chapter.id}-${chapter.title}-${
                    'publishDate' in chapter ? chapter.publishDate : ''
                  }`}
                  languageVersionTranslationProgress={
                    activeLanguageVersion
                      ? translationProgressData?.progressByLanguage[
                          activeLanguageVersion
                        ]?.progressByChapterId?.[chapter.id]
                      : undefined
                  }
                  previewType={previewType}
                  previewVersion={previewVersion}
                />
              ))}
            </div>
          </div>

          {previewType === 'course' || Boolean(activeLanguageVersion) ? null : (
            <div className="actions">
              <div className="actions-add-chapter">
                <AddChapter disabled={Boolean(previewType)} />
              </div>

              <EditorChaptersOrder
                chapters={chapters}
                currentChapter={currentChapter}
                disabled={Boolean(previewType)}
              />

              <Button
                category="light"
                disabled={Boolean(previewType || activeLanguageVersion)}
                onClick={handleEditorView}
                icon="history"
                tooltip={messages.coursesCourseVersionHistory}
              />
            </div>
          )}
        </>
      ) : null}

      <style jsx>{`
        .chapters {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          flex-grow: 0;
          flex-shrink: 1;
          margin-right: 20px;
          width: 290px;
        }
        .header {
          align-items: center;
          border-bottom: 1px solid ${theme.lightGreyDark};
          border-radius: ${`${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`};
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 700;
          padding: 10px 15px;
          position: relative;
        }
        .header-sidebar {
          display: none;
        }
        .header-toggle {
          cursor: pointer;
          display: none;
          outline: 0;
        }
        .header-toggle:focus {
          box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
        }
        .header-icon {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          position: absolute;
          top: 12px;
          right: 15px;
        }
        .active-chapter {
          display: none;
        }
        .chapters-list {
          border-radius: ${`${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`};
          overflow: hidden;
        }
        .chapters-list.without-actions {
          border-radius: ${theme.cardBorderRadius};
        }
        .actions {
          align-items: center;
          border-top: 1px solid ${theme.lightGreyDark};
          display: flex;
          gap: 10px;
          padding: 15px;
        }
        @media (max-width: ${breakpoints.medium - 1}px) {
          .chapters {
            width: 100%;
          }
          .header-toggle {
            display: block;
          }
          .active-chapter.visible {
            display: block;
          }
          .chapters-list {
            display: none;
          }
          .chapters-list.expanded {
            display: block;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .chapters {
            display: flex;
            flex-direction: column;
            position: sticky;
            top: 10px;
            max-height: calc(100vh - 40px);
          }
          .header-sidebar {
            display: block;
          }
          .chapters-list {
            background: linear-gradient(white 30%, rgba(255, 255, 255, 0)),
              linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
              radial-gradient(
                50% 0,
                farthest-side,
                rgba(0, 0, 0, 0.2),
                rgba(0, 0, 0, 0)
              ),
              radial-gradient(
                  50% 100%,
                  farthest-side,
                  rgba(0, 0, 0, 0.2),
                  rgba(0, 0, 0, 0)
                )
                0 100%;
            background: linear-gradient(white 30%, rgba(255, 255, 255, 0)),
              linear-gradient(rgba(255, 255, 255, 0), white 70%) 0 100%,
              radial-gradient(
                farthest-side at 50% 0,
                rgba(0, 0, 0, 0.2),
                rgba(0, 0, 0, 0)
              ),
              radial-gradient(
                  farthest-side at 50% 100%,
                  rgba(0, 0, 0, 0.2),
                  rgba(0, 0, 0, 0)
                )
                0 100%;
            background-attachment: local, local, scroll, scroll;
            background-color: white;
            background-position-y: 0px, 100%, 1px, 100%;
            background-repeat: no-repeat;
            background-size: 100% 40px, 100% 40px, 100% 14px, 100% 14px;
            overflow: auto;
          }
          .actions {
            display: flex;
          }
          .actions-add-chapter {
            flex-grow: 1;
          }
          .actions-add-chapter :global(button) {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default memo(EditorChapters);
